import CircularProgress from '@mui/material/CircularProgress';
import Icon from '__core/modules/atoms/Icons/Icon';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import React from 'react';
import { Link } from 'react-router-dom';

const Button = ({
  ariaDescribedBy,
  ariaDisabled,
  ariaExpanded,
  ariaHasPopup,
  borderRadius = 'default',
  className,
  color = 'primary',
  dataTestid,
  disabled = false,
  id,
  icon,
  iconColor = 'inherit',
  iconHtmlColor,
  iconPosition = 'default',
  iconRight,
  iconSize = 'small',
  loading = false,
  onClick,
  onlyIcon,
  size = 'default',
  text = 'Title',
  theme = 'default',
  to,
  type = 'button',
  variant = 'default',
}) => {
  function renderButton() {
    return (
      <button
        aria-busy={loading || undefined}
        aria-disabled={ariaDisabled || undefined}
        aria-describedby={ariaDescribedBy}
        aria-expanded={ariaHasPopup ? !!ariaExpanded : undefined}
        aria-haspopup={ariaHasPopup}
        aria-label={text}
        data-testid={dataTestid}
        disabled={disabled}
        id={id}
        // Un botón que se anuncia deshabilitado no dispara: `ariaDisabled` lo deja enfocable, no
        // accionable. Es lo mismo que hace el cierre del Drawer y lo que espera quien lo usa.
        onClick={ariaDisabled ? undefined : onClick}
        title={text}
        type={type}
        className={clsx(
          'button',
          `button--${variant}`,
          `button--size-${size}`,
          `button--color-${color}`,
          `button--border-radius-${borderRadius}`,
          `button--icon-position-${iconPosition}`,
          {
            'button--color-only-icon': onlyIcon,
            [`button--theme-${theme}`]: theme,
          },
          className
        )}
      >
        {loading ? (
          <div className="button__spinner">
            <CircularProgress color="inherit" size={23} />
          </div>
        ) : (
          <>
            {
              <div className="button__content">
                {icon && <Icon color={disabled ? 'disabled' : iconColor} fontSize={iconSize} htmlColor={iconHtmlColor} type={icon} />}
                {!onlyIcon && <div className="button__text">{text}</div>}
                {iconRight && (
                  <span className="button__icon-right">
                    <Icon color={disabled ? 'disabled' : iconColor} fontSize={iconSize} htmlColor={iconHtmlColor} type={iconRight} />
                  </span>
                )}
              </div>
            }
          </>
        )}
      </button>
    );
  }

  return to ? <Link to={to}>{renderButton()}</Link> : renderButton();
};

Button.propTypes = {
  ariaDescribedBy: PropTypes.string,
  ariaDisabled: PropTypes.bool,
  ariaExpanded: PropTypes.bool,
  ariaHasPopup: PropTypes.oneOf(['menu', 'listbox', 'tree', 'grid', 'dialog', 'true']),
  borderRadius: PropTypes.oneOf(['default', 'big', 'small']),
  color: PropTypes.oneOf([
    'black',
    'border',
    'error',
    'grey',
    'info',
    'orange',
    'primary',
    'primary-outlined',
    'success',
    'transparent',
    'transparent-primary',
    'warning',
    'white',
  ]),
  disabled: PropTypes.bool,
  icon: PropTypes.string,
  iconColor: PropTypes.oneOf(['inherit', 'action', 'disabled', 'primary', 'secondary', 'error']),
  iconHtmlColor: PropTypes.string,
  iconPosition: PropTypes.oneOf(['default', 'left', 'right']),
  iconRight: PropTypes.string,
  iconSize: PropTypes.oneOf(['inherit', 'large', 'medium', 'small']),
  id: PropTypes.string,
  size: PropTypes.oneOf(['default', 'small', 'xs', 'large']),
  text: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  theme: PropTypes.oneOf(['default', 'google']),
  type: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  variant: PropTypes.oneOf(['default', 'inline']),
};

export default Button;
